import { useMemo, useState, type ReactNode } from 'react'
import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd'
import { ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react'
import { Skeleton } from '@repo/ui'

import { InfoTooltip } from 'components/feedback/InfoTooltip'

/**
 * VRG-734 — a drag-and-drop board of lanes, extracted from GTM → Sales' deals board (VRG-691/692)
 * on its second use (VERAfied episodes), per working-conventions §36. The deals board now renders
 * through this; what is deal-specific (cards, the evidence rules, the Lost confirm) stays there.
 *
 * Mechanics it owns:
 *   - a column per lane, then an optional TRAILING lane (Lost, Shelved) that collapses to a narrow
 *     strip with its count, expanding on demand;
 *   - a drop the caller's `verdict` refuses dims that lane and explains why on hover;
 *   - the board scrolls sideways INSIDE its own container rather than squeezing lanes below
 *     `laneMin` (the page itself never scrolls sideways).
 */

export type KanbanVerdict = { kind: 'ok' } | { kind: 'refused'; reason: string }

export interface KanbanLane<L extends string> {
  id: L
  label: string
  /** Glossary id for the lane's info tooltip (§44). */
  term?: string
}

export interface KanbanTrailingLane<L extends string> extends KanbanLane<L> {
  expanded: boolean
  onToggle: () => void
  /** The strip's label while a card is being dragged ("Drop to mark lost"). */
  dropHint: string
}

export interface KanbanBoardProps<T, L extends string> {
  lanes: readonly KanbanLane<L>[]
  trailing?: KanbanTrailingLane<L>
  items: readonly T[]
  getId: (item: T) => string
  /** The lane an item sits in; `null` leaves it off the board. */
  laneOf: (item: T) => L | null
  renderCard: (item: T) => ReactNode
  canDrag: boolean
  /** Whether `item` may be dropped on `to`. Omit to allow every move. */
  verdict?: (item: T, from: L, to: L) => KanbanVerdict
  /** An allowed drop onto a different lane. */
  onDrop: (item: T, from: L, to: L) => void
  /** The second line under a lane's title; default `"<n>"`. */
  laneSummary?: (lane: L, items: T[]) => string
  emptyLabel?: (lane: L) => string
  laneMin?: number
  testId?: string
}

/** The collapsed trailing lane's width: a strip with its count. */
const STRIP = 52
const GAP = 12

export const KanbanBoard = <T, L extends string>({
  lanes,
  trailing,
  items,
  getId,
  laneOf,
  renderCard,
  canDrag,
  verdict,
  onDrop,
  laneSummary,
  emptyLabel,
  laneMin = 156,
  testId = 'kanban',
}: KanbanBoardProps<T, L>) => {
  const theme = useTheme()
  const [dragging, setDragging] = useState<T | null>(null)
  const all = useMemo(() => (trailing ? [...lanes, trailing] : [...lanes]), [lanes, trailing])
  const byLane = useMemo(() => {
    const m = new Map<L, T[]>(all.map((l) => [l.id, [] as T[]]))
    for (const it of items) {
      const lane = laneOf(it)
      if (lane !== null) m.get(lane)?.push(it)
    }
    return m
  }, [all, items, laneOf])

  const check = (item: T, to: L): KanbanVerdict | null => {
    const from = laneOf(item)
    if (from === null || from === to) return null
    return verdict ? verdict(item, from, to) : { kind: 'ok' }
  }

  const end = (r: DropResult) => {
    const item = items.find((i) => getId(i) === r.draggableId)
    setDragging(null)
    if (!item || !r.destination) return
    const from = laneOf(item)
    const to = r.destination.droppableId as L
    if (from === null || from === to) return
    const v = check(item, to)
    if (v?.kind === 'refused') return
    onDrop(item, from, to)
  }

  const showTrailing = trailing?.expanded ?? false
  const columns = `repeat(${lanes.length}, minmax(${laneMin}px, 1fr))${trailing ? (showTrailing ? ` minmax(${laneMin}px, 1fr)` : ` ${STRIP}px`) : ''}`
  const minWidth = laneMin * lanes.length + (trailing ? (showTrailing ? laneMin : STRIP) : 0) + GAP * (all.length - 1)

  return (
    <DragDropContext onDragStart={(s) => setDragging(items.find((i) => getId(i) === s.draggableId) ?? null)} onDragEnd={end}>
      <Box sx={{ overflowX: 'auto', pb: 1 }} data-testid={testId}>
        <Box sx={{ display: 'grid', gridTemplateColumns: columns, gap: 3, minWidth }}>
          {all.map((lane) => {
            const cards = byLane.get(lane.id) ?? []
            const v = dragging ? check(dragging, lane.id) : null
            const refused = v?.kind === 'refused'
            const isTrailing = trailing?.id === lane.id
            const collapsed = isTrailing && !showTrailing
            return (
              <Droppable droppableId={lane.id} key={lane.id} isDropDisabled={refused || !canDrag}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    data-testid={`${testId}-lane-${lane.id}`}
                    sx={{
                      borderRadius: 3,
                      p: collapsed ? 1 : 2,
                      minHeight: 240,
                      border: isTrailing ? `1px dashed ${theme.palette.primary[200]}` : `1px solid transparent`,
                      backgroundColor: snapshot.isDraggingOver
                        ? alpha(isTrailing ? theme.palette.error.main : theme.palette.brand[500], 0.08)
                        : theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.03)
                          : theme.palette.grey[50],
                      opacity: dragging && refused ? 0.5 : 1,
                      transition: 'background-color 120ms, opacity 120ms',
                      overflow: 'hidden',
                    }}
                  >
                    {collapsed && trailing ? (
                      <Stack alignItems="center" spacing={2} sx={{ pt: 1 }}>
                        <Tooltip title={`Show ${lane.label.toLowerCase()} (${cards.length})`} placement="left">
                          <IconButton
                            size="small"
                            onClick={trailing.onToggle}
                            aria-label={`Show ${lane.label.toLowerCase()}`}
                            data-testid={`${testId}-show-${lane.id}`}
                          >
                            <ChevronsLeftIcon size={16} strokeWidth={1} />
                          </IconButton>
                        </Tooltip>
                        <Typography variant="body3" color="text.secondary">
                          {cards.length}
                        </Typography>
                        <Typography
                          variant="body3"
                          sx={{ fontWeight: 500, writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}
                        >
                          {dragging && laneOf(dragging) !== lane.id ? trailing.dropHint : lane.label}
                        </Typography>
                        <Box sx={{ display: 'none' }}>{provided.placeholder}</Box>
                      </Stack>
                    ) : (
                      <>
                        <Stack spacing={1} sx={{ px: 1, pb: 3, pt: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                            <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
                              {lane.label}
                            </Typography>
                            {lane.term && <InfoTooltip term={lane.term} />}
                            {isTrailing && trailing && (
                              <Tooltip title={`Hide ${lane.label.toLowerCase()}`}>
                                <IconButton
                                  size="small"
                                  onClick={trailing.onToggle}
                                  aria-label={`Hide ${lane.label.toLowerCase()}`}
                                  data-testid={`${testId}-hide-${lane.id}`}
                                  sx={{ ml: 'auto' }}
                                >
                                  <ChevronsRightIcon size={16} strokeWidth={1} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                          <Typography variant="body3" color="text.secondary" noWrap>
                            {dragging && refused ? 'Not here: see the tooltip' : laneSummary ? laneSummary(lane.id, cards) : String(cards.length)}
                          </Typography>
                        </Stack>
                        <Tooltip
                          title={dragging && v?.kind === 'refused' ? v.reason : ''}
                          placement="top"
                          open={!!(dragging && refused && snapshot.isDraggingOver)}
                        >
                          <Stack spacing={2}>
                            {cards.map((item, i) => (
                              <Draggable draggableId={getId(item)} index={i} key={getId(item)} isDragDisabled={!canDrag}>
                                {(p) => (
                                  <Box ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}>
                                    {renderCard(item)}
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                            {cards.length === 0 && (
                              <Typography variant="body3" color="text.secondary" sx={{ px: 1, py: 4, textAlign: 'center' }}>
                                {emptyLabel ? emptyLabel(lane.id) : 'Nothing here'}
                              </Typography>
                            )}
                          </Stack>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                )}
              </Droppable>
            )
          })}
        </Box>
      </Box>
    </DragDropContext>
  )
}

/** §19 — the board's footprint: `lanes` columns of card-shaped bars, then the collapsed strip. */
export const KanbanBoardSkeleton = ({ lanes, trailing = true, laneMin = 156 }: { lanes: number; trailing?: boolean; laneMin?: number }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${lanes}, minmax(${laneMin}px, 1fr))${trailing ? ` ${STRIP}px` : ''}`,
        gap: 3,
        minWidth: laneMin * lanes + (trailing ? STRIP : 0) + GAP * (trailing ? lanes : lanes - 1),
      }}
    >
      {Array.from({ length: lanes }, (_, l) => (
        <Stack key={l} spacing={2} sx={{ p: 2, minHeight: 240 }}>
          <Skeleton height={16} width="60%" />
          <Skeleton height={14} width="40%" />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={112} />
          ))}
        </Stack>
      ))}
      {trailing && <Skeleton variant="rounded" height={240} />}
    </Box>
  </Box>
)
